import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import {
  encryptToken,
  encryptRefreshToken,
  encryptEmail,
  encryptRole,
  encryptUser,
} from 'shared/lib/tokenEncryption';
import { refreshUserPermissions } from '../../shared/lib/refreshUserPermissions';

const usePostLoginSetup = () => {
  const { t } = useTranslation('common');
  const [isSettingUp, setIsSettingUp] = useState(false);

  // Persist identity from the LOGIN response. `not_editable` lives ONLY here
  // (absent from GET /me), so it must be captured at login — the /me-driven
  // permission refresh never rewrites the `role` key.
  const storeLoginIdentity = (data) => {
    localStorage.setItem('token', encryptToken(data?.access_token));
    localStorage.setItem(
      'refresh_token',
      encryptRefreshToken(data?.refresh_token)
    );
    localStorage.setItem('email', encryptEmail(data?.user?.email));
    localStorage.setItem(
      'role',
      encryptRole({
        role: data?.user?.role,
        not_editable: data?.company_role?.not_editable,
      })
    );
    localStorage.setItem('user', encryptUser(data?.user));
    localStorage.setItem(
      'selectedLanguage',
      data?.user?.language_preference?.toLowerCase() || 'en'
    );
    localStorage.setItem('name', data?.user?.name);
  };

  const resolveFirstAllowedPath = (menusArray) => {
    const first = menusArray?.[0];
    return (
      first?.sub_menus?.[0]?.frontend_endpoint ??
      first?.frontend_endpoint ??
      '/'
    );
  };

  const executeSetup = async (loginResponse) => {
    setIsSettingUp(true);

    try {
      const data = loginResponse?.data;
      storeLoginIdentity(data);

      // Permissions (menus + company) come solely from GET /me now.
      // A /me failure at login is fatal — without permissions the user cannot
      // proceed, so surface the error and clear the partial session.
      let menusArray = [];
      try {
        menusArray = await refreshUserPermissions();
      } catch {
        toast.error(t('login_failed_local_storage'));
        localStorage.clear();
        return;
      }

      const isDataStored =
        localStorage.getItem('token') &&
        localStorage.getItem('email') &&
        localStorage.getItem('menus') &&
        localStorage.getItem('name');

      if (isDataStored) {
        window.location.href = resolveFirstAllowedPath(menusArray);
      } else {
        toast.error(t('login_failed_local_storage'));
        localStorage.clear();
      }
    } finally {
      setIsSettingUp(false);
    }
  };

  return {
    executeSetup,
    isSettingUp,
  };
};

export default usePostLoginSetup;
